"use client";

import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useRouter } from "next/navigation";
import Link from "next/link";
import type { RouterOutputs } from "@ept/api";
import { useTranslations } from "use-intl";
type Job = RouterOutputs["jobsRouter"]["getJobsForInstitution"]["data"][number];
interface JobsProps {
  institutionSlug: string;
  jobs: Job[];
  locale: "en" | "ro" | "es" | null;
}
const isJobActive = (job: Job) => {
  return job.expirationDate && job.expirationDate.getTime() >= new Date().getTime();
};
const getExpirationDate = (job: Job) => {
  const expirationDays = (job.expirationDate!.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24);
  if (expirationDays < 1) {
    return "azi";
  }
  return " în " + expirationDays.toFixed(0) + " zile";
};
export const JobCards: React.FC<JobsProps> = ({
  institutionSlug,
  jobs,
  locale
}) => {
  const t = useTranslations();
  const router = useRouter();
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCounty, setSelectedCounty] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const handleCardClick = (job: Job) => {
    router.push(`/uat/${institutionSlug}/transparency/jobs/${job.slug}`);
  };
  useEffect(() => {
    function filterJobs() {
      let filtered = jobs.filter(job => job.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) && (selectedCounty === "All" || job.location === selectedCounty) && (!startDate || job.expirationDate! >= new Date(startDate)) && (!endDate || job.expirationDate! <= new Date(endDate)));
      setFilteredJobs(filtered);
    }
    filterJobs();
  }, [jobs, searchTerm, selectedCounty, startDate, endDate]);
  const activeJobs = filteredJobs.filter(job => isJobActive(job));
  const inactiveJobs = filteredJobs.filter(job => !isJobActive(job));
  return <div data-sentry-component="JobCards" data-sentry-source-file="JobCards.tsx">
      <div className="mb-4 flex w-full items-center justify-between gap-4">
        <div className="ml-4 text-base font-semibold text-gray-700">
          {t("job_count_20230802124516", {
          count: filteredJobs.length
        })}{" "}
          {filteredJobs.length > 1 ? t("public_jobs_20230802124516") : ""}
        </div>
        <Input placeholder={t("search_placeholder_20230802123503")} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white" data-sentry-element="Input" data-sentry-source-file="JobCards.tsx" />
      </div>
      {activeJobs.length > 0 && <>
          <div className="mb-6 mt-6 flex w-full items-center gap-2 px-4 py-2 drop-shadow-xl">
            <span className="mr-2 text-xl uppercase">
              {t("src_app_uat_institutionSlug_transparency_components_JobCards_tsx_1")}
            </span>
            <div className="flex-grow border-t-2 border-gray-300"></div>
          </div>
          <div className="grid grid-cols-1 gap-4 overflow-visible desktop:grid-cols-2">
            {activeJobs.map((job, index) => <Link href={`/uat/${institutionSlug}/transparency/jobs/${job.slug}`} key={index}>
                <Card className="flex cursor-pointer items-end justify-between gap-4 rounded-3xl bg-white p-6 transition-all hover:bg-amber-50">
                  <div className="flex flex-col gap-4">
                    <div>
                      <div className="text-xl font-bold">{job.jobTitle}</div>
                      <p className="text-base text-gray-700">{job.employerName}</p>
                    </div>
                    <div className="flex">
                      <span className="mr-2 flex gap-2 rounded-full bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700">
                        <MapPin size={20} strokeWidth={3} /> {job.location}
                      </span>
                      <span className={`mr-2 inline-block rounded-full px-3 py-2 text-sm font-semibold ${job.type === "Permanent" ? "bg-yellow-500" : "bg-blue-500 text-white"}`}>
                        {job.type}
                      </span>
                    </div>
                  </div>
                  {job.expirationDate && <div className="flex flex-col items-end">
                      <p className="text-sm text-gray-600">
                        {t("published_on_20230802124516", {
                  date: job.publicationDate.toLocaleDateString(locale || "en")
                })}
                      </p>
                      <p className="text-sm text-gray-600">
                        {t("expires_on_20230802124517", {
                  date: getExpirationDate(job)
                })}
                      </p>
                    </div>}
                </Card>
              </Link>)}
          </div>
        </>}
      {inactiveJobs.length > 0 && <>
          <div className="mb-6 mt-12 flex w-full items-center gap-2 px-4 py-2 drop-shadow-xl">
            <span className="mr-2 text-xl uppercase">
              {t("src_app_uat_institutionSlug_transparency_components_JobCards_tsx_4")}
            </span>
            <div className="flex-grow border-t-2 border-gray-300"></div>
          </div>
          <div className="grid grid-cols-1 gap-4 overflow-visible desktop:grid-cols-2">
            {inactiveJobs.map((job, index) => <Card key={index} className="flex cursor-pointer items-end justify-between gap-4 rounded-3xl bg-white p-6 transition-all hover:bg-amber-50" onClick={() => handleCardClick(job)}>
                <div className="flex flex-col gap-4">
                  <div>
                    <div className="text-xl font-bold">{job.jobTitle}</div>
                    <p className="text-base text-gray-700">{job.employerName}</p>
                  </div>
                  <div className="flex">
                    <span className="mr-2 flex gap-2 rounded-full bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700">
                      <MapPin size={20} strokeWidth={3} /> {job.location}
                    </span>
                    <span className={`mr-2 inline-block rounded-full px-3 py-2 text-sm font-semibold ${job.type === "Permanent" ? "bg-yellow-500" : "bg-blue-500 text-white"}`}>
                      {job.type}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <p className="text-sm text-gray-600">
                    {t("published_on_20230802124516", {
                date: job.publicationDate.toLocaleDateString(locale || "en")
              })}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t("src_app_uat_institutionSlug_transparency_components_JobCards_tsx_6")}
                  </p>
                </div>
              </Card>)}
          </div>
        </>}
    </div>;
};